import {
  healthFormSchema,
  memberHealthFormSchema,
  participantHealthSchema,
  submitHealthAnswersResponseSchema,
  type HealthForm,
  type MemberHealthForm,
  type ParticipantHealth,
  type SubmitHealthAnswersRequest,
  type UpdateHealthFormRequest,
} from "@teamna/shared";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import type { z } from "zod";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * Health notes and the terms at join (C2, migration 0077). The owner's setup (Settings, Health),
 * one person's flag or answers for the team (the server decides who reads the answers), and the
 * member's step at join. The phone has the same hooks in `apps/mobile/src/hooks/use-health.ts`.
 */

/** GET /owner/businesses/:id/health-form */
export function useHealthForm(businessId: string | null): UseQueryResult<HealthForm> {
  return useQuery({
    queryKey: queryKeys.owner.healthForm(businessId ?? "none"),
    queryFn: () => api.get(`/owner/businesses/${businessId ?? ""}/health-form`, healthFormSchema),
    enabled: Boolean(businessId),
  });
}

/** PUT /owner/businesses/:id/health-form. The answer is the form as it is now. */
export function useUpdateHealthForm(businessId: string): UseMutationResult<HealthForm, unknown, UpdateHealthFormRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.put(`/owner/businesses/${businessId}/health-form`, input, healthFormSchema),
    onSuccess: (form) => {
      queryClient.setQueryData(queryKeys.owner.healthForm(businessId), form);
    },
  });
}

/** GET /business/businesses/:id/participants/:pid/health: the flag, and the answers for a reader who may read them. */
export function useParticipantHealth(businessId: string | null, participantId: string | null, enabled = true): UseQueryResult<ParticipantHealth> {
  return useQuery({
    queryKey: queryKeys.owner.participantHealth(businessId ?? "none", participantId ?? "none"),
    queryFn: () =>
      api.get(`/business/businesses/${businessId ?? ""}/participants/${participantId ?? ""}/health`, participantHealthSchema),
    enabled: enabled && Boolean(businessId) && Boolean(participantId),
    staleTime: 30 * 1000,
  });
}

/** GET /me/businesses/:id/health-form: the questions, the terms, and the people still to answer. */
export function useMemberHealthForm(businessId: string | null): UseQueryResult<MemberHealthForm> {
  return useQuery({
    queryKey: queryKeys.memberHealthForm(businessId ?? "none"),
    queryFn: () => api.get(`/me/businesses/${businessId ?? ""}/health-form`, memberHealthFormSchema),
    enabled: Boolean(businessId),
  });
}

export type HealthAnswersSaved = z.infer<typeof submitHealthAnswersResponseSchema>;

/** POST /me/businesses/:id/health-form: the answers of one person. */
export function useSubmitHealthAnswers(businessId: string): UseMutationResult<HealthAnswersSaved, unknown, SubmitHealthAnswersRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`/me/businesses/${businessId}/health-form`, input, submitHealthAnswersResponseSchema),
    onSuccess: (saved) => {
      queryClient.setQueryData<MemberHealthForm>(queryKeys.memberHealthForm(businessId), (current) =>
        current ? { ...current, pending: saved.pending } : current,
      );
    },
  });
}
